/**
 * @file SiteClosedBanner.tsx
 * @description Плашка «Сайт закрыт для покупателей» над шапкой кабинета
 * магазина (phase19.16, SB138; uiux-site-closed.md §4, §7;
 * db-design-site-closed.md §6). Стоит в слоте `AppShell.banner` под полосой
 * входа под сотрудником, на всех страницах, включая конструктор. Тон
 * «внимание» (SC26) — как у полосы входа под сотрудником.
 *
 * Состояния (модель — `siteClosedBanner`):
 *  - пауза магазина: «с 14:20 · Иван · 42 мин · откроется сам в 15:20»
 *    [Смотреть сайт] [Открыть сайт] — счётчик обновляется раз в 30 с;
 *  - до срока ≤ 10 мин: «Сайт откроется через N минут» [Продлить на 1 ч];
 *  - без срока дольше 3 суток — предупреждение о поиске;
 *  - пауза платформы — текст, открыть может только платформа;
 *  - без права настроек — без «Открыть», с пояснением;
 *  - phase19.28: пауза «запуск» — `LaunchBanner` («Магазин ещё не открыт»).
 * На 360 — текст в несколько строк, кнопки под ним во всю ширину (48 px).
 */

import { CirclePause } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { LaunchBanner } from "./LaunchBanner";
import { useSiteAccessActions } from "../../hooks/site-access/useSiteAccessActions";
import type { SiteAccessHandle } from "../../hooks/site-access/useSiteAccess";
import { pluralRu } from "../../lib/plural";
import { siteClosedBanner, type ShopBannerModel } from "../../lib/site-access/site-access-view";

/** Как часто пересчитывать «N мин» и «≤ 10 мин до срока». */
export const SITE_CLOSED_BANNER_TICK_MS = 30_000;

/** Без права настроек — кто может открыть. */
export const SITE_OPEN_RIGHT_NOTE = "Открыть может владелец или сотрудник с правом настроек";

/** Подпись области плашки и объявления диктору при смене вида. */
const REGION_LABEL = "Доступ к сайту";
const CLOSED_ANNOUNCE = "Сайт закрыт для покупателей.";
const SOON_ANNOUNCE = "Сайт скоро откроется сам — меньше чем через 10 минут.";

const BANNER_CLASS =
  "flex flex-wrap items-center gap-x-3 gap-y-2 bg-[var(--warning-soft)] px-4 py-2 text-on-surface [box-shadow:inset_0_-1px_0_var(--warning-line)]";

/** Перерисовка раз в `ms` — для счётчика времени. */
function useTick(ms: number): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), ms);
    return () => window.clearInterval(timer);
  }, [ms]);
}

/** Строки текста паузы магазина: заголовок, «с 14:20 · Иван · 42 мин», срок. */
function shopLines(m: ShopBannerModel): { title: string; prefix: string; tail: string | null; extra: string | null } {
  if (m.soonMinutes !== null) {
    return {
      title: `Сайт откроется через ${m.soonMinutes} ${pluralRu(m.soonMinutes, ["минуту", "минуты", "минут"])}`,
      prefix: `Закрыт для покупателей с ${m.since}`,
      tail: null,
      extra: null,
    };
  }
  return {
    title: "Сайт закрыт для покупателей",
    prefix: `с ${m.since}`,
    tail: m.elapsed,
    extra: m.autoOpen ? `Откроется сам ${m.autoOpen}` : "Без срока — откройте сайт, когда закончите",
  };
}

export function SiteClosedBanner({ access }: { access: SiteAccessHandle }) {
  useTick(SITE_CLOSED_BANNER_TICK_MS);
  const actions = useSiteAccessActions(access);
  const model = siteClosedBanner(access.state, access.serverNow());
  if (!model) return null;

  if (model.kind === "launch") return <LaunchBanner model={model} access={access} />;

  if (model.kind === "platform") {
    return (
      <div role="region" aria-label={REGION_LABEL} data-testid="site-closed-banner" data-kind="platform" className={BANNER_CLASS}>
        <CirclePause className="size-5 shrink-0 text-warning" aria-hidden />
        <p role="status" className="type-body min-w-0 flex-1 break-words">
          {model.text}
        </p>
      </div>
    );
  }

  const lines = shopLines(model);
  const soon = model.soonMinutes !== null;
  return (
    <div role="region" aria-label={REGION_LABEL} data-testid="site-closed-banner" data-kind={soon ? "soon" : "shop"} className={BANNER_CLASS}>
      {/* Диктору — только смена вида плашки, а не каждое «42 мин» (счётчик идёт в регионе без живого объявления). */}
      <span role="status" className="sr-only">
        {soon ? SOON_ANNOUNCE : CLOSED_ANNOUNCE}
      </span>
      <CirclePause className="size-5 shrink-0 self-start text-warning max-md:mt-0.5 md:self-center" aria-hidden />
      {/* До 768 — строками (заголовок, кто и сколько, срок); 768–1279 — две строки; с 1280 — одна. */}
      <div className="type-body min-w-0 flex-1">
        <strong className="block font-semibold xl:inline">{lines.title}</strong>
        <span className="block xl:inline">
          <span className="hidden xl:inline"> · </span>
          {lines.prefix}
          {model.by ? (
            <>
              {" · "}
              {/* Длинное имя — многоточием, остальное не уезжает. */}
              <span className="inline-block max-w-[12rem] truncate align-bottom" title={model.by}>
                {model.by}
              </span>
            </>
          ) : null}
          {lines.tail ? ` · ${lines.tail}` : null}
          {lines.extra ? (
            <>
              <span className="hidden md:inline"> · </span>
              <span className="block md:inline">{lines.extra}</span>
            </>
          ) : null}
        </span>
        {model.longPause ? (
          <span className="block text-on-surface-variant">
            Сайт закрыт больше 3 суток — поисковики могут понизить его в выдаче.
          </span>
        ) : null}
        {!model.canManage ? <span className="block text-on-surface-variant">{SITE_OPEN_RIGHT_NOTE}</span> : null}
      </div>
      <div className="flex shrink-0 gap-2 max-md:w-full">
        <Button variant="outline" className="max-md:flex-1" loading={actions.busy === "watch"} onClick={() => actions.watch()}>
          Смотреть сайт
        </Button>
        {model.canManage && soon ? (
          <Button className="max-md:flex-1" loading={actions.busy === "extend"} onClick={() => void actions.extend(model.closureId)}>
            Продлить на 1 ч
          </Button>
        ) : null}
        {model.canManage && !soon ? (
          <Button className="max-md:flex-1" loading={actions.busy === "open"} onClick={() => void actions.open(model.closureId)}>
            Открыть сайт
          </Button>
        ) : null}
      </div>
    </div>
  );
}
